/**
 * fogvalsguiden.js — Fogvalsguiden (wizard) + fristående kalkylatorsida (Agent C).
 * Vanilla ES2019, ingen build-step, laddas med defer. Driver BÅDA sektionerna:
 *   [data-jl-fogguide]  sections/fogvalsguiden.liquid (4-stegs-wizard + resultat)
 *   [data-jl-fogkalk]   sections/fogkalkylator.liquid (/pages/kalkylator)
 *
 * Produktdata hämtas från templates/collection.guide-data.liquid via
 *   /collections/<handle>?view=guide-data&page=N  (loopas tills next === false)
 * och cachas i sessionStorage (15 min TTL).
 *
 * Beroenden (Agent G):
 *   window.Jolat.money / displayOre / getTaxMode  — pris i valt momsläge
 *   window.Jolat.cart.add                          — lägg i varukorgen + öppna drawer
 *   event 'jolat:tax:changed'                      — re-rendera alla priser
 *   window.JolatKalk (assets/kalkylator-core.js)   — åtgångsformler (kalkylatorsidan)
 *
 * Prisrendering följer DOM-kontraktet i snippets/price.liquid: guidens kort
 * bygger .jl-price-markup i JS men låter renderingen ske via Jolat (öre +
 * data-pro-pct), så pro-pris och momsväxling fungerar utan dubblerad logik.
 */
(function () {
  'use strict';

  /* ============================================================== */
  /* Gemensamt: datakälla (guide-data) + hjälpare                    */
  /* ============================================================== */

  var CACHE_TTL_MS = 15 * 60 * 1000;
  var PAGE_SAFETY_LIMIT = 40; // 40 × 250 = 10 000 produkter, säkerhetsspärr

  // Produkttyper som INTE är fogmassor och ska uteslutas ur guide-/kalk-träffar
  // (SPEC §1). Normaliserade (gemener/trim) så att en avvikande admin-stavning
  // — "verktyg & tillbehör", " Primer " e.d. — fortfarande filtreras bort.
  var ACCESSORY_TYPES = ['verktyg', 'tillbehör', 'rengöring', 'primer'];

  /** True om produktens typ är ett tillbehör (normaliserad jämförelse). */
  function isAccessoryType(typ) {
    var t = String(typ == null ? '' : typ).trim().toLowerCase();
    if (!t) return false;
    for (var i = 0; i < ACCESSORY_TYPES.length; i++) {
      // Exakt ELLER innehåller — fångar "verktyg & tillbehör", "rengöringsmedel".
      if (t === ACCESSORY_TYPES[i] || t.indexOf(ACCESSORY_TYPES[i]) !== -1) return true;
    }
    return false;
  }

  function cacheKey(url) { return 'jolat_guidedata_' + url; }

  function readCache(url) {
    try {
      var raw = window.sessionStorage.getItem(cacheKey(url));
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed.t !== 'number') return null;
      if (Date.now() - parsed.t > CACHE_TTL_MS) return null;
      return parsed.d;
    } catch (e) { return null; }
  }

  function writeCache(url, data) {
    try {
      window.sessionStorage.setItem(cacheKey(url), JSON.stringify({ t: Date.now(), d: data }));
    } catch (e) { /* full eller privat läge — strunta */ }
  }

  /**
   * Hämtar HELA guide-datasetet (alla sidor) för en kollektion.
   * Returnerar en Promise<Array<product>>. Resultatet cachas i sessionStorage.
   */
  var datasetPromises = {};
  function loadDataset(collectionUrl) {
    if (datasetPromises[collectionUrl]) return datasetPromises[collectionUrl];

    var cached = readCache(collectionUrl);
    if (cached) {
      datasetPromises[collectionUrl] = Promise.resolve(cached);
      return datasetPromises[collectionUrl];
    }

    var sep = collectionUrl.indexOf('?') > -1 ? '&' : '?';

    function fetchPage(page, acc) {
      var url = collectionUrl + sep + 'view=guide-data&page=' + page;
      // OBS: skicka INTE Accept: application/json — då returnerar Shopify sitt
      // inbyggda collection-JSON ({collection:{...}}) i stället för att rendera
      // vår ?view=guide-data-mall ({products:[...]}). res.json() parsar ändå
      // svaret (mallen kör {% layout none %} och returnerar ren JSON).
      return window.fetch(url, { credentials: 'same-origin' })
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.json();
        })
        .then(function (json) {
          var products = (json && json.products) || [];
          var next = acc.concat(products);
          if (json && json.next === true && page < PAGE_SAFETY_LIMIT) {
            return fetchPage(page + 1, next);
          }
          return next;
        });
    }

    datasetPromises[collectionUrl] = fetchPage(1, []).then(function (all) {
      writeCache(collectionUrl, all);
      return all;
    }).catch(function (err) {
      delete datasetPromises[collectionUrl]; // tillåt nytt försök
      throw err;
    });

    return datasetPromises[collectionUrl];
  }

  /** Läser JSON ur ett <script type="application/json">-element. */
  function readStrings(el) {
    if (!el) return {};
    try { return JSON.parse(el.textContent) || {}; } catch (e) { return {}; }
  }

  function fillTemplate(template, map) {
    var out = template || '';
    Object.keys(map).forEach(function (key) {
      out = out.split(key).join(map[key]);
    });
    return out;
  }

  /** Escapar HTML för säker innerHTML-injektion (produktdata kommer från Liquid | json men kan innehålla < & "). */
  function esc(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
    /** True om värdet kan tolkas som ett tal (rörelse kan komma som number eller "25.0"). */
  function hasNum(value) { return !isNaN(parseFloat(value)); }

  /** Tal utan onödig ".0", decimaler med svensk komma: 25.0 → "25", 12.5 → "12,5". */
  function formatNum(value) {
    var n = parseFloat(value);
    if (isNaN(n)) return '';
    return (Math.round(n) === n) ? String(Math.round(n)) : String(n).replace('.', ',');
  }

  /**
   * Brandklass-fältet innehåller ofta bara Euroklass "E" / "E (Klass E)" (reaktion
   * vid brand) som INTE säger något om brandtätning. Dölj bruset — visa bara
   * meningsfulla klasser (EI…, B1/B2/B3, DIN m.m.) eller '' när inget vettigt.
   */
  function meaningfulBrand(value) {
    var s = String(value == null ? '' : value).trim();
    if (!s) return '';
    if (/^e(\s*\(klass\s*e\))?$/i.test(s)) return '';
    return s;
  }

  /** True om värdet kan tolkas som ett tal (rörelse kan komma som number eller "25.0"). */
  function hasNum(value) { return !isNaN(parseFloat(value)); }

  /** Tal utan onödig ".0", decimaler med svensk komma: 25.0 → "25", 12.5 → "12,5". */
  function formatNum(value) {
    var n = parseFloat(value);
    if (isNaN(n)) return '';
    return (Math.round(n) === n) ? String(Math.round(n)) : String(n).replace('.', ',');
  }

  /**
   * Brandklass-fältet innehåller ofta bara Euroklass "E" / "E (Klass E)" (reaktion
   * vid brand) som INTE säger något om brandtätning. Dölj det bruset — returnera
   * bara meningsfulla klasser (EI…, B1/B2/B3, DIN m.m.) eller '' när inget vettigt.
   */
  function meaningfulBrand(value) {
    var s = String(value == null ? '' : value).trim();
    if (!s) return '';
    if (/^e(\s*\(klass\s*e\))?$/i.test(s)) return '';
    return s;
  }

  /** Pris i öre inkl. moms → formaterat i valt momsläge. Faller tillbaka snyggt om Jolat saknas. */
  function fmtPrice(ore) {
    if (window.Jolat && typeof window.Jolat.money === 'function') {
      return window.Jolat.money(window.Jolat.displayOre(ore));
    }
    return Math.round((ore || 0) / 100) + ' kr';
  }

  function taxLabel(strings) {
    var mode = (window.Jolat && window.Jolat.getTaxMode) ? window.Jolat.getTaxMode() : 'incl';
    return mode === 'excl' ? (strings.taxExcl || 'exkl. moms') : (strings.taxIncl || 'inkl. moms');
  }
  /**
   * Bildfallback när produkten saknar foto: en dämpad, varumärkesfärgad platta
   * med en typ-medveten produktikon + produkttyp (i st.f. en tom grå ruta).
   * Delas av guidens och kalkylatorns kort (båda bygger korten i JS).
   * Ikonen är inline-SVG eftersom korten inte renderas via Liquid (snippets/icon
   * går inte att {% render %} härifrån) — patron för fog/lim, låga för brandtätning.
   */
  function noimgIconPaths(typ) {
    var t = String(typ == null ? '' : typ).toLowerCase();
    if (t.indexOf('brand') !== -1) {
      // Låga (samma form som snippets/icon.liquid 'eld') — brandtätning.
      return '<path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.4-.5-2-1-3-1.1-2.1-.2-4.1 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.2.4-2.3 1-3a2.5 2.5 0 0 0 2.5 2.5z"/>';
    }
    // Patron/cartridge — fogmassa, lim och övrigt.
    return '<path d="M8 9.5h8V20a1.5 1.5 0 0 1-1.5 1.5h-5A1.5 1.5 0 0 1 8 20z"/>' +
      '<path d="M8.4 9.5 10 6.3h4l1.6 3.2"/>' +
      '<path d="M10.6 6.3V3.8h2.8V6.3"/>' +
      '<path d="M12 3.8V2.2"/>' +
      '<path d="M9.6 13.8h4.8"/>';
  }

  function noimgMarkup(p) {
    var typ = (p && p.typ) || 'Produkt';
    return '<span class="jl-fg-card__noimg" role="img" aria-label="' + esc(p.title) + '">' +
      '<svg class="jl-fg-card__noimg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
      noimgIconPaths(typ) + '</svg>' +
      '<span class="jl-fg-card__noimg-type">' + esc(typ) + '</span>' +
    '</span>';
  }

  /**
   * Bildfallback när produkten saknar foto: en dämpad, varumärkesfärgad platta
   * med en typ-medveten produktikon + produkttyp (i st.f. en tom grå ruta).
   * Delas av guidens och kalkylatorns kort (båda bygger korten i JS).
   * Ikonen är inline-SVG eftersom korten inte renderas via Liquid (snippets/icon
   * går inte att {% render %} härifrån) — patron för fog/lim, låga för brandtätning.
   */
  function noimgIconPaths(typ) {
    var t = String(typ == null ? '' : typ).toLowerCase();
    if (t.indexOf('brand') !== -1) {
      // Låga (samma form som snippets/icon.liquid 'eld') — brandtätning.
      return '<path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.4-.5-2-1-3-1.1-2.1-.2-4.1 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.2.4-2.3 1-3a2.5 2.5 0 0 0 2.5 2.5z"/>';
    }
    // Patron/cartridge — fogmassa, lim och övrigt.
    return '<path d="M8 9.5h8V20a1.5 1.5 0 0 1-1.5 1.5h-5A1.5 1.5 0 0 1 8 20z"/>' +
      '<path d="M8.4 9.5 10 6.3h4l1.6 3.2"/>' +
      '<path d="M10.6 6.3V3.8h2.8V6.3"/>' +
      '<path d="M12 3.8V2.2"/>' +
      '<path d="M9.6 13.8h4.8"/>';
  }

  function noimgMarkup(p) {
    var typ = (p && p.typ) || 'Produkt';
    return '<span class="jl-fg-card__noimg" role="img" aria-label="' + esc(p.title) + '">' +
      '<svg class="jl-fg-card__noimg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
      noimgIconPaths(typ) + '</svg>' +
      '<span class="jl-fg-card__noimg-type">' + esc(typ) + '</span>' +
    '</span>';
  }

  /** Lagerstatus-pill (samma regel som product-card). p.lager = riktigt saldo. */
  function stockMarkup(p, strings) {
    var cls, txt;
    if (p.available === false) { cls = 'jl-stock--out'; txt = strings.soldOut || 'Tillfälligt slut'; }
    else if (p.lager === false) { cls = 'jl-stock--backorder'; txt = strings.backorder || 'Beställningsvara'; }
    else { cls = 'jl-stock--in'; txt = strings.inStock || 'I lager'; }
    return '<span class="jl-stock ' + cls + '">' + esc(txt) + '</span>';
  }

  /**
   * Bygger ett pris-element som följer momsläge (sätter data-jolat-money så att
   * jolat.js uppdaterar det vid 'jolat:tax:changed'). Pro-pris hanteras inte i
   * guide-datasetet (priserna är listpris inkl. moms) — listpris visas korrekt.
   */
  function priceMarkup(ore, strings, fromPrefix) {
    var prefix = fromPrefix ? '<span class="jl-price__from">' + esc(strings.from || 'Från') + '</span> ' : '';
    return '<span class="jl-price">' + prefix +
      '<span class="jl-price__current" data-jolat-money data-ore="' + Math.round(ore || 0) + '">' + esc(fmtPrice(ore)) + '</span> ' +
      '<span class="jl-price__tax">' + esc(taxLabel(strings)) + '</span></span>';
  }

  /**
   * Förpackningsvolym (ml) för "antal förpackningar"-uppskattning på kalkylatorsidan.
   * Avser samma variant som faktiskt läggs i varukorgen (första köpbara, se
   * guide-data-mallen). Resolutionsordning enligt SPEC §1:
   *   1. variant-metafält fogspec.volym_ml (p.volym, satt i guide-data)
   *   2. parsa ur Storlek-texten (p.storlek = varianttiteln) med densitet
   *   3. 310 ml-fallback (vanligaste patron)
   */
  function guessPackVolume(p) {
    // 1. Färdigberäknad volym ur variant-metafält.
    var direct = parseFloat(p && p.volym);
    if (direct && direct > 0) return direct;
    // 2. Parsa ur Storlek-texten (kg/g → ml via produktens densitet).
    if (p && p.storlek && window.JolatKalk && typeof window.JolatKalk.parseVolumeMl === 'function') {
      var dens = parseFloat(p.densitet) || null;
      var v = window.JolatKalk.parseVolumeMl(p.storlek, dens);
      if (v && v > 0) return v;
    }
    // 3. Fallback.
    return 310;
  }

  /* ============================================================== */
  /* DEL 1 — FOGVALSGUIDEN (wizard)                                  */
  /* ============================================================== */

  function initGuide(root) {
    var collectionUrl = root.getAttribute('data-collection-url');
    var phone = root.getAttribute('data-phone') || '';
    var strings = readStrings(root.querySelector('[data-jl-fogguide-strings]'));

    var TOTAL_STEPS = 4;
    var KEYS = ['applikation', 'substrat', 'funktion', 'miljo'];
    var state = { applikation: [], substrat: [], funktion: [], miljo: [] };
    var currentStep = 1;
    var dataset = null;
    var showMoreWrap = null; // referens till "Visa fler"-knappens wrapper
    var showMoreWrap = null; // referens till "Visa fler"-knappens wrapper (renderResults)

    var form = root.querySelector('.jl-fg-form');
    var wizard = root.querySelector('[data-jl-fg-wizard]');
    var results = root.querySelector('[data-jl-fg-results]');
    var grid = root.querySelector('[data-jl-fg-grid]');
    var statusEl = root.querySelector('[data-jl-fg-status]');
    var nearmiss = root.querySelector('[data-jl-fg-nearmiss]');
    var nearmissGrid = root.querySelector('[data-jl-fg-nearmiss-grid]');
    var callCard = root.querySelector('[data-jl-fg-call]');
    var resultsTitle = root.querySelector('[data-jl-fg-results-title]');

    /* ---- Steg-navigering ---- */
    function fieldsetFor(step) { return root.querySelector('[data-jl-step="' + step + '"]'); }

    /* ---- Mjuk scroll vid stegbyte (respekterar prefers-reduced-motion) ---- */
    // Höjden på det som ligger sticky i toppen just nu: fokuslägets minimala
    // toppbar under guidesteget, annars den vanliga headern i resultatvyn. Så
    // hamnar varken progress-baren eller nästa rubrik bakom den fasta raden.
    function topStickyOverlap() {
      var overlap = 0;
      function consider(el) {
        if (!el) return;
        var cs = window.getComputedStyle(el);
        if (cs.display === 'none' || cs.visibility === 'hidden') return;
        if (cs.position !== 'sticky' && cs.position !== 'fixed') return;
        overlap = Math.max(overlap, el.offsetHeight || 0);
      }
      consider(root.querySelector('[data-jl-fg-topbar]'));
      var header = document.querySelector('.jl-header');
      consider(header && header.closest('.shopify-section'));
      return overlap + 12; // liten luft under den sticky raden
    }

    function scrollToGuideTop(target) {
      if (!target || !window.scrollTo) return;
      var top = target.getBoundingClientRect().top + window.pageYOffset - topStickyOverlap();
      if (top < 0) top = 0;
      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      try { window.scrollTo({ top: top, behavior: reduce ? 'auto' : 'smooth' }); }
      catch (e) { window.scrollTo(0, top); }
    }

    function showStep(n, doScroll) {
      if (n < 1 || n > TOTAL_STEPS) return;
      currentStep = n;
      if (results) results.hidden = true;
      if (wizard) wizard.hidden = false;
      // Fokusläge: under guidesteget döljs den vanliga headern (se fogvalsguiden.css).
      document.body.classList.remove('jl-guide-results');
      root.querySelectorAll('[data-jl-step]').forEach(function (fs) {
        fs.hidden = parseInt(fs.getAttribute('data-jl-step'), 10) !== n;
      });
      root.querySelectorAll('[data-jl-progress]').forEach(function (li) {
        var p = parseInt(li.getAttribute('data-jl-progress'), 10);
        li.classList.toggle('is-active', p === n);
        li.classList.toggle('is-completed', p < n);
        if (p === n) li.setAttribute('aria-current', 'step');
        else li.removeAttribute('aria-current');
      });
      // Flytta fokus till stegets rubrik (tabindex=-1) UTAN webbläsarens egen
      // scroll — vår mjuka scroll nedan sköter positioneringen i stället.
      var legend = fieldsetFor(n) && fieldsetFor(n).querySelector('.jl-fg-step__title');
      if (legend && legend.focus) { try { legend.focus({ preventScroll: true }); } catch (e) { legend.focus(); } }
      // Vid stegövergång (next/back/restart): scrolla upp till progress-baren.
      // Hoppas över vid första render/deep-link så att sidan inte hoppar vid load.
      if (doScroll) scrollToGuideTop(wizard);
    }

    /* ---- Input-hantering ---- */
    function syncStateFromInputs(fs) {
      var key = fs.getAttribute('data-key');
      var multi = fs.getAttribute('data-multi') === 'true';
      var checked = Array.prototype.filter.call(fs.querySelectorAll('input'), function (i) { return i.checked; });
      state[key] = checked.map(function (i) { return i.value; });
      if (!multi) {
        // radio: aktivera Nästa
        var nextBtn = fs.querySelector('[data-jl-action="next"]');
        if (nextBtn) nextBtn.disabled = state[key].length === 0;
      }
    }

    function enforceMax(fs, changedInput) {
      var max = parseInt(fs.getAttribute('data-max'), 10);
      if (!max) return;
      var checked = Array.prototype.filter.call(fs.querySelectorAll('input'), function (i) { return i.checked; });
      if (checked.length > max) {
        // avmarkera det äldsta (inte det just klickade)
        for (var i = 0; i < checked.length; i++) {
          if (checked[i] !== changedInput) { checked[i].checked = false; break; }
        }
      }
    }

    form.addEventListener('change', function (e) {
      var input = e.target;
      if (!input || input.tagName !== 'INPUT') return;
      var fs = input.closest('[data-jl-step]');
      if (!fs) return;
      enforceMax(fs, input);
      syncStateFromInputs(fs);
    });

    /* ---- Knappar ---- */
    root.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-jl-action]');
      if (!btn || !root.contains(btn)) return;
      var action = btn.getAttribute('data-jl-action');
      if (action === 'next') {
        if (currentStep === 1 && state.applikation.length === 0) return;
        showStep(currentStep + 1, true);
      } else if (action === 'back') {
        if (currentStep > 1) showStep(currentStep - 1, true);
      } else if (action === 'finish') {
        runResults();
        scrollToGuideTop(results);
      } else if (action === 'restart') {
        restart();
      } else if (action === 'share') {
        shareUrl(btn);
      }
    });

    /* ---- Filtrering + sortering (klientsida) ---- */
    function hasAll(productList, wanted) {
      if (!wanted.length) return true;
      if (!Array.isArray(productList)) return false;
      for (var i = 0; i < wanted.length; i++) {
        if (productList.indexOf(wanted[i]) === -1) return false;
      }
      return true;
    }

    // "Isocyanatfri" matchar både miljo- och funktion-listan (SPEC §6-C).
    function matchMiljo(p, wanted) {
      if (!wanted.length) return true;
      for (var i = 0; i < wanted.length; i++) {
        var val = wanted[i];
        var inMiljo = Array.isArray(p.miljo) && p.miljo.indexOf(val) !== -1;
        var inFunktion = (val === 'Isocyanatfri') && Array.isArray(p.funktion) && p.funktion.indexOf(val) !== -1;
        if (!inMiljo && !inFunktion) return false;
      }
      return true;
    }

    function isAccessory(p) {
      return isAccessoryType(p.typ);
    }

    function matchCount(p) {
      // antal matchade funktion + miljö (för sortering)
      var n = 0;
      state.funktion.forEach(function (v) { if (Array.isArray(p.funktion) && p.funktion.indexOf(v) !== -1) n++; });
      state.miljo.forEach(function (v) {
        if ((Array.isArray(p.miljo) && p.miljo.indexOf(v) !== -1) ||
            (v === 'Isocyanatfri' && Array.isArray(p.funktion) && p.funktion.indexOf(v) !== -1)) n++;
      });
      return n;
    }

    function sortMatches(a, b) {
      // 1. i lager först
      var sa = a.lager === true ? 1 : 0;
      var sb = b.lager === true ? 1 : 0;
      if (sa !== sb) return sb - sa;
      // 2. flest matchade funktion+miljö
      var ma = matchCount(a), mb = matchCount(b);
      if (ma !== mb) return mb - ma;
      // 3. teknisk lämplighet: betygsatt rörelse före obetygsatt, högre först.
      //    null/NaN → -1 så en betygsatt elastisk fog rankas över en styv/obetygsatt.
      var ra = hasNum(a.rorelse) ? parseFloat(a.rorelse) : -1;
      var rb = hasNum(b.rorelse) ? parseFloat(b.rorelse) : -1;
      if (ra !== rb) return rb - ra;
      // 4. pris asc (sista tiebreak → prisvärd vinnare vid likvärdig teknik)
      var pa = parseFloat(a.pris_fran) || 0;
      var pb = parseFloat(b.pris_fran) || 0;
      return pa - pb;
    }

    // Nära träffar: rangordna efter hur NÄRA produkten är att uppfylla allt
    // användaren valde. Funktion/miljö är släppta som FILTER här, så "hur många
    // av de valda kraven produkten ändå uppfyller" är det viktigaste — inte lager.
    function sortNear(a, b) {
      // 1. flest uppfyllda funktion+miljö-krav först (= minst antal saknade).
      //    matchCount() är samma räkning som missRows() bygger på (inkl. Isocyanatfri),
      //    och eftersom alla kort jämförs mot samma valda totalsumma är "flest uppfyllda"
      //    identiskt med "minst saknade".
      var ma = matchCount(a), mb = matchCount(b);
      if (ma !== mb) return mb - ma;
      // 2. i lager först (tiebreak när korten ligger lika nära kravbilden).
      var sa = a.lager === true ? 1 : 0;
      var sb = b.lager === true ? 1 : 0;
      if (sa !== sb) return sb - sa;
      // 3. teknisk lämplighet: betygsatt rörelse före obetygsatt, högre först.
      var ra = hasNum(a.rorelse) ? parseFloat(a.rorelse) : -1;
      var rb = hasNum(b.rorelse) ? parseFloat(b.rorelse) : -1;
      if (ra !== rb) return rb - ra;
      // 4. pris asc (sista tiebreak).
      var pa = parseFloat(a.pris_fran) || 0;
      var pb = parseFloat(b.pris_fran) || 0;
      return pa - pb;
    }

    function filterStrict(products) {
      var app = state.applikation[0];
      return products.filter(function (p) {
        if (isAccessory(p)) return false;
        if (app && !(Array.isArray(p.applikation) && p.applikation.indexOf(app) !== -1)) return false;
        if (!hasAll(p.substrat, state.substrat)) return false;
        if (!hasAll(p.funktion, state.funktion)) return false;
        if (!matchMiljo(p, state.miljo)) return false;
        return true;
      });
    }

    // Nära träffar: släpp funktion + miljö-kraven (behåll applikation + substrat).
    function filterNear(products) {
      var app = state.applikation[0];
      return products.filter(function (p) {
        if (isAccessory(p)) return false;
        if (app && !(Array.isArray(p.applikation) && p.applikation.indexOf(app) !== -1)) return false;
        if (!hasAll(p.substrat, state.substrat)) return false;
        return true;
      });
    }

    /* ---- Resultatkort ---- */
    function whyRows(p) {
      // Max 4 ✓-rader av de val produkten faktiskt uppfyller.
      var rows = [];
      var app = state.applikation[0];
      if (app && Array.isArray(p.applikation) && p.applikation.indexOf(app) !== -1) rows.push(app);
      state.substrat.forEach(function (v) { if (Array.isArray(p.substrat) && p.substrat.indexOf(v) !== -1) rows.push(v); });
      state.funktion.forEach(function (v) { if (Array.isArray(p.funktion) && p.funktion.indexOf(v) !== -1) rows.push(v); });
      state.miljo.forEach(function (v) {
        if ((Array.isArray(p.miljo) && p.miljo.indexOf(v) !== -1) ||
            (v === 'Isocyanatfri' && Array.isArray(p.funktion) && p.funktion.indexOf(v) !== -1)) rows.push(v);
      });
      return rows.slice(0, 4);
    }

    // Ouppfyllda krav (för nära träffar): vilka valda funktion/miljö produkten saknar.
    function missRows(p) {
      var rows = [];
      state.funktion.forEach(function (v) { if (!(Array.isArray(p.funktion) && p.funktion.indexOf(v) !== -1)) rows.push(v); });
      state.miljo.forEach(function (v) {
        var ok = (Array.isArray(p.miljo) && p.miljo.indexOf(v) !== -1) ||
                 (v === 'Isocyanatfri' && Array.isArray(p.funktion) && p.funktion.indexOf(v) !== -1);
        if (!ok) rows.push(v);
      });
      return rows.slice(0, 4);
    }

    /* ---- Differentiering: kontext + utmärkande egenskaper ---- */
    // Beräknas en gång per resultatlista så korten kan jämföras MOT VARANDRA
    // (lägst pris, högst rörelse …) i stället för att bara upprepa de valda kriterierna.
    function computeCtx(list) {
      var minPrice = Infinity, maxPrice = -Infinity, minR = Infinity, maxR = -Infinity;
      list.forEach(function (p) {
        var pr = parseFloat(p.pris_fran);
        if (!isNaN(pr)) { if (pr < minPrice) minPrice = pr; if (pr > maxPrice) maxPrice = pr; }
        if (hasNum(p.rorelse)) { var r = parseFloat(p.rorelse); if (r < minR) minR = r; if (r > maxR) maxR = r; }
      });
      return {
        count: list.length,
        minPrice: minPrice, maxPrice: maxPrice,
        minR: (minR === Infinity ? null : minR),
        maxR: (maxR === -Infinity ? null : maxR)
      };
    }

    // Sann bara om produkten är billigast OCH det finns verklig prisspridning i listan.
    function isCheapest(p, ctx) {
      if (!ctx || ctx.count < 2) return false;
      var pr = parseFloat(p.pris_fran);
      return !isNaN(pr) && pr === ctx.minPrice && ctx.minPrice !== ctx.maxPrice;
    }

    // Chips som SKILJER korten åt (kemibas, rörelse, övermålning, riktig brandklass, lägst pris).
    function diffChipsMarkup(p, ctx) {
      var chips = '';
      if (p.polymerbas) chips += '<span class="jl-chip">' + esc(p.polymerbas) + '</span>';
      if (hasNum(p.rorelse)) chips += '<span class="jl-chip jl-chip--spec">' + esc((strings.movement || 'Rörelse') + ' ±' + formatNum(p.rorelse) + ' %') + '</span>';
      if (String(p.overmalningsbar) === 'true') chips += '<span class="jl-chip jl-chip--spec">' + esc(strings.paintable || 'Övermålningsbar') + '</span>';
      var brand = meaningfulBrand(p.brandklass);
      if (brand) chips += '<span class="jl-chip jl-chip--fire">' + esc(brand) + '</span>';
      if (isCheapest(p, ctx)) chips += '<span class="jl-chip jl-chip--accent">' + esc(strings.lowestPrice || 'Lägst pris') + '</span>';
      return chips;
    }

    // En kort "varför just den" som lyfter det mest särskiljande draget mot övriga träffar.
    function standoutReason(p, ctx) {
      if (isCheapest(p, ctx)) return strings.standoutCheapest || 'Lägst pris av de som passar';
      if (hasNum(p.rorelse) && ctx.maxR !== null && ctx.minR !== null && ctx.maxR > ctx.minR && parseFloat(p.rorelse) === ctx.maxR) {
        return (strings.standoutMovement || 'Tål mest rörelse') + ' (±' + formatNum(p.rorelse) + ' %)';
      }
      if (String(p.overmalningsbar) === 'true') return strings.standoutPaint || 'Kan övermålas';
      var brand = meaningfulBrand(p.brandklass);
      if (brand) return (strings.standoutFire || 'Brandklassad') + ' (' + brand + ')';
      if (hasNum(p.rorelse)) return (strings.standoutMovementHas || 'Elastisk fog') + ' (±' + formatNum(p.rorelse) + ' %)';
      if (p.polymerbas) return p.polymerbas;
      return strings.inStock || 'I lager';
    }

    /* ---- Differentiering: kontext + utmärkande egenskaper ---- */
    // Beräknas en gång per resultatlista så korten kan jämföras MOT VARANDRA
    // (lägst pris, högst rörelse …) i stället för att bara upprepa de valda kriterierna.
    function computeCtx(list) {
      var minPrice = Infinity, maxPrice = -Infinity, minR = Infinity, maxR = -Infinity;
      list.forEach(function (p) {
        var pr = parseFloat(p.pris_fran);
        if (!isNaN(pr)) { if (pr < minPrice) minPrice = pr; if (pr > maxPrice) maxPrice = pr; }
        if (hasNum(p.rorelse)) { var r = parseFloat(p.rorelse); if (r < minR) minR = r; if (r > maxR) maxR = r; }
      });
      return {
        count: list.length,
        minPrice: minPrice, maxPrice: maxPrice,
        minR: (minR === Infinity ? null : minR),
        maxR: (maxR === -Infinity ? null : maxR)
      };
    }

    // Sann bara om produkten är billigast OCH det finns verklig prisspridning i listan.
    function isCheapest(p, ctx) {
      if (!ctx || ctx.count < 2) return false;
      var pr = parseFloat(p.pris_fran);
      return !isNaN(pr) && pr === ctx.minPrice && ctx.minPrice !== ctx.maxPrice;
    }

    // Chips som SKILJER korten åt (kemibas, rörelse, övermålning, riktig brandklass, lägst pris).
    function diffChipsMarkup(p, ctx) {
      var chips = '';
      if (p.polymerbas) chips += '<span class="jl-chip">' + esc(p.polymerbas) + '</span>';
      if (hasNum(p.rorelse)) chips += '<span class="jl-chip jl-chip--spec">' + esc((strings.movement || 'Rörelse') + ' ±' + formatNum(p.rorelse) + ' %') + '</span>';
      if (String(p.overmalningsbar) === 'true') chips += '<span class="jl-chip jl-chip--spec">' + esc(strings.paintable || 'Övermålningsbar') + '</span>';
      var brand = meaningfulBrand(p.brandklass);
      if (brand) chips += '<span class="jl-chip jl-chip--fire">' + esc(brand) + '</span>';
      if (isCheapest(p, ctx)) chips += '<span class="jl-chip jl-chip--accent">' + esc(strings.lowestPrice || 'Lägst pris') + '</span>';
      return chips;
    }

    // En kort "varför just den" som lyfter det mest särskiljande draget mot övriga träffar.
    function standoutReason(p, ctx) {
      if (isCheapest(p, ctx)) return strings.standoutCheapest || 'Lägst pris av de som passar';
      if (hasNum(p.rorelse) && ctx.maxR !== null && ctx.minR !== null && ctx.maxR > ctx.minR && parseFloat(p.rorelse) === ctx.maxR) {
        return (strings.standoutMovement || 'Tål mest rörelse') + ' (±' + formatNum(p.rorelse) + ' %)';
      }
      if (String(p.overmalningsbar) === 'true') return strings.standoutPaint || 'Kan övermålas';
      var brand = meaningfulBrand(p.brandklass);
      if (brand) return (strings.standoutFire || 'Brandklassad') + ' (' + brand + ')';
      if (hasNum(p.rorelse)) return (strings.standoutMovementHas || 'Elastisk fog') + ' (±' + formatNum(p.rorelse) + ' %)';
      if (p.polymerbas) return p.polymerbas;
      return strings.inStock || 'I lager';
    }

    function cardMarkup(p, opts) {
      opts = opts || {};
      var ctx = opts.ctx || { count: 0 };
      var img = p.img
        ? '<img class="jl-fg-card__img" src="' + esc(p.img) + '" alt="' + esc(p.title) + '" loading="lazy" width="400" height="400">'
        : noimgMarkup(p);

      var chips = diffChipsMarkup(p, ctx);

      var reasons, reasonClass, reasonTitle;
      if (opts.miss) {
        reasons = missRows(p);
        reasonClass = 'jl-fg-card__why--miss';
        reasonTitle = strings.missingLabel || 'Uppfyller inte:';
      } else {
        reasons = whyRows(p);
        reasonClass = '';
        reasonTitle = strings.why || 'Därför passar den:';
      }
      var iconMark = opts.miss ? '✗' : '✓';
      var reasonRows = reasons.map(function (r) {
        return '<li><span class="jl-fg-card__tick" aria-hidden="true">' + iconMark + '</span>' + esc(r) + '</li>';
      }).join('');
      var whyBlock = reasons.length
        ? '<div class="jl-fg-card__why ' + reasonClass + '"><p class="jl-fg-card__why-title">' + esc(reasonTitle) + '</p><ul role="list">' + reasonRows + '</ul></div>'
        : '';

      // "Sticker ut"-rad: visas för riktiga träffar (inte för nära-träff-kort med ✗).
      var standoutBlock = opts.miss ? ''
        : '<p class="jl-fg-card__standout"><span class="jl-fg-card__standout-label">' + esc(strings.standout || 'Sticker ut:') + '</span> ' + esc(standoutReason(p, ctx)) + '</p>';
      // Topprekommendation = första (bäst rankade) kortet.
      var recBadge = opts.top
        ? '<span class="jl-fg-rec-badge">' + esc(strings.recommended || 'Vår rekommendation') + '</span>'
        : '';
      var articleClass = 'jl-card jl-fg-card' + (opts.top ? ' jl-fg-card--recommended' : '');

      var calcUrl = p.url + '#kalkylator';
      return '<article class="' + articleClass + '">' +
        '<a class="jl-fg-card__media" href="' + esc(p.url) + '">' + img + '</a>' +
        '<div class="jl-fg-card__body">' +
          recBadge +
          (p.vendor ? '<span class="jl-fg-card__vendor">' + esc(p.vendor) + '</span>' : '') +
          '<a class="jl-fg-card__title" href="' + esc(p.url) + '">' + esc(p.title) + '</a>' +
          (chips ? '<div class="jl-fg-card__chips">' + chips + '</div>' : '') +
          standoutBlock +
          stockMarkup(p, strings) +
          '<div class="jl-fg-card__pricerow">' + priceMarkup(p.pris_fran, strings, true) + '</div>' +
          whyBlock +
          '<div class="jl-fg-card__actions">' +
            '<a class="jl-btn jl-btn--full" href="' + esc(p.url) + '">' + esc(strings.showProduct || 'Visa produkt') + '</a>' +
            '<a class="jl-btn jl-btn--secondary jl-btn--full" href="' + esc(calcUrl) + '">' + esc(strings.calcUsage || 'Räkna åtgång') + '</a>' +
          '</div>' +
        '</div>' +
      '</article>';
    }

    function renderCards(container, list, opts) {
      container.innerHTML = list.map(function (p) { return cardMarkup(p, opts); }).join('');
    }

    function setStatus(text) {
      if (statusEl) statusEl.textContent = text || '';
    }

    /* ---- Kör resultatvyn ---- */
    function showResultsView() {
      if (wizard) wizard.hidden = true;
      if (results) results.hidden = false;
      // Resultatläge: full navigation tillbaka (visar åter den vanliga headern).
      document.body.classList.add('jl-guide-results');
      if (resultsTitle && resultsTitle.focus) { try { resultsTitle.focus({ preventScroll: true }); } catch (e) {} }
    }

    /* ---- "Visa fler" (fäller ut resten av träffarna) ---- */
    function removeShowMore() {
      if (showMoreWrap && showMoreWrap.parentNode) showMoreWrap.parentNode.removeChild(showMoreWrap);
      showMoreWrap = null;
    }

    function addShowMore(matches, initial, ctx) {
      removeShowMore();
      var remaining = matches.length - initial;
      showMoreWrap = document.createElement('div');
      showMoreWrap.className = 'jl-fg-showmore';
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'jl-btn jl-btn--secondary';
      btn.textContent = (strings.showMore || 'Visa fler') + ' (' + remaining + ')';
      btn.addEventListener('click', function () {
        var extra = matches.slice(initial).map(function (p) { return cardMarkup(p, { ctx: ctx }); }).join('');
        grid.insertAdjacentHTML('beforeend', extra);
        removeShowMore();
      });
      showMoreWrap.appendChild(btn);
      if (grid.parentNode) grid.parentNode.insertBefore(showMoreWrap, grid.nextSibling);
    }

    /* ---- "Visa fler" (fäller ut resten av träffarna) ---- */
    function removeShowMore() {
      if (showMoreWrap && showMoreWrap.parentNode) showMoreWrap.parentNode.removeChild(showMoreWrap);
      showMoreWrap = null;
    }

    function addShowMore(matches, initial, ctx) {
      removeShowMore();
      var remaining = matches.length - initial;
      showMoreWrap = document.createElement('div');
      showMoreWrap.className = 'jl-fg-showmore';
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'jl-btn jl-btn--secondary';
      btn.textContent = (strings.showMore || 'Visa fler') + ' (' + remaining + ')';
      btn.addEventListener('click', function () {
        var extra = matches.slice(initial).map(function (p) { return cardMarkup(p, { ctx: ctx }); }).join('');
        grid.insertAdjacentHTML('beforeend', extra);
        removeShowMore();
      });
      showMoreWrap.appendChild(btn);
      if (grid.parentNode) grid.parentNode.insertBefore(showMoreWrap, grid.nextSibling);
    }

    function renderResults() {
      var matches = filterStrict(dataset).sort(sortMatches);
      grid.innerHTML = '';
      removeShowMore();
      nearmiss.hidden = true;
      callCard.hidden = true;

      if (matches.length > 0) {
        var ctx = computeCtx(matches);
        var INITIAL = 6;
        // Topprekommendation (bäst rankad) + upp till 5 fler initialt.
        var head = cardMarkup(matches[0], { top: true, ctx: ctx });
        var rest = matches.slice(1, INITIAL).map(function (p) { return cardMarkup(p, { ctx: ctx }); }).join('');
        grid.innerHTML = head + rest;
        if (matches.length > INITIAL) addShowMore(matches, INITIAL, ctx);
        var tmpl = strings.resultCount || '[antal] produkter passar dina val';
        setStatus(fillTemplate(tmpl, { '[antal]': String(matches.length) }));
      } else {
        setStatus(strings.noResults || 'Inga produkter matchar alla dina krav');
        // Nära träffar — släpp funktion/miljö-kraven, markera vad som inte uppfylls med ✗.
        var near = filterNear(dataset).sort(sortNear).slice(0, 6);
        if (near.length > 0) {
          var nctx = computeCtx(near);
          renderCards(nearmissGrid, near, { miss: true, ctx: nctx });
          nearmiss.hidden = false;
        }
        callCard.hidden = false;
      }
      showResultsView();
    }

    function runResults() {
      showResultsView();
      grid.innerHTML = '';
      setStatus(strings.loading || 'Hämtar produkter …');
      pushShareUrl();
      loadDataset(collectionUrl).then(function (data) {
        dataset = data;
        renderResults();
      }).catch(function () {
        setStatus(strings.error || 'Kunde inte hämta produktdata. Ladda om sidan och försök igen.');
      });
    }

    /* ---- Delbar URL + restart ---- */
    function buildQuery() {
      var params = new URLSearchParams();
      KEYS.forEach(function (k) {
        state[k].forEach(function (v) { params.append(k, v); });
      });
      return params.toString();
    }

    function pushShareUrl() {
      try {
        var qs = buildQuery();
        var url = window.location.pathname + (qs ? '?' + qs : '');
        window.history.replaceState({}, '', url);
      } catch (e) { /* ignore */ }
    }

    function shareUrl(btn) {
      var qs = buildQuery();
      var url = window.location.origin + window.location.pathname + (qs ? '?' + qs : '');
      var done = function () {
        if (!btn) return;
        var original = btn.textContent;
        btn.textContent = strings.copied || 'Länk kopierad';
        setTimeout(function () { btn.textContent = original; }, 1800);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(done, done);
      } else {
        try {
          var ta = document.createElement('textarea');
          ta.value = url; ta.setAttribute('readonly', '');
          ta.style.position = 'absolute'; ta.style.left = '-9999px';
          document.body.appendChild(ta); ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
        } catch (e) {}
        done();
      }
    }

    function restart() {
      KEYS.forEach(function (k) { state[k] = []; });
      form.querySelectorAll('input').forEach(function (i) { i.checked = false; });
      var nextBtn = fieldsetFor(1) && fieldsetFor(1).querySelector('[data-jl-action="next"]');
      if (nextBtn) nextBtn.disabled = true;
      try { window.history.replaceState({}, '', window.location.pathname); } catch (e) {}
      showStep(1, true);
    }

    /* ---- Läs querystring vid load (?applikation=, ?substrat= …) ---- */
    function applyQuery() {
      var params = new URLSearchParams(window.location.search);
      var any = false;
      KEYS.forEach(function (k) {
        var values = params.getAll(k);
        if (!values.length) return;
        var fs = root.querySelector('[data-jl-step][data-key="' + k + '"]');
        if (!fs) return;
        var multi = fs.getAttribute('data-multi') === 'true';
        var picked = multi ? values : values.slice(0, 1);
        picked.forEach(function (val) {
          var input = fs.querySelector('input[value="' + cssEscapeValue(val) + '"]');
          if (input) { input.checked = true; any = true; }
        });
        syncStateFromInputs(fs);
      });
      return { any: any, hasApplikation: state.applikation.length > 0 };
    }

    // Enkel attributvärde-escape för querySelector (värdena är kända metafält-strängar med &, /, parenteser).
    function cssEscapeValue(value) {
      if (window.CSS && CSS.escape) return CSS.escape(value);
      return String(value).replace(/["\\]/g, '\\$&');
    }

    /* ---- Init ---- */
    var q = applyQuery();
    if (q.hasApplikation) {
      // Startsidan länkar med ?applikation= → hoppa direkt till steg 2 (SPEC §6-C).
      // Om även andra steg fyllts (delad URL) → visa resultat direkt.
      var deepLink = state.substrat.length || state.funktion.length || state.miljo.length;
      if (deepLink) { runResults(); }
      else { showStep(2); }
    } else {
      showStep(1);
    }

    document.addEventListener('jolat:tax:changed', function () {
      // Priser uppdateras av jolat.js (data-jolat-money). Bara tax-label-spans
      // som vi byggt utan data-attribut behöver uppdateras.
      root.querySelectorAll('.jl-price__tax').forEach(function (el) { el.textContent = taxLabel(strings); });
    });
  }

  /* ============================================================== */
  /* DEL 2 — FRISTÅENDE KALKYLATORSIDA (/pages/kalkylator)           */
  /* ============================================================== */

  function initKalk(root) {
    var collectionUrl = root.getAttribute('data-collection-url');
    var strings = readStrings(root.querySelector('[data-jl-fogkalk-strings]'));

    var dataset = null;
    var datasetError = false;
    var currentApp = null;

    var inputs = {
      bredd: root.querySelector('[data-jl-fk-input="bredd"]'),
      djup: root.querySelector('[data-jl-fk-input="djup"]'),
      langd: root.querySelector('[data-jl-fk-input="langd"]'),
      spill: root.querySelector('[data-jl-fk-input="spill"]')
    };
    var volumeOut = root.querySelector('[data-jl-fk-out="volume"]');
    var backerOut = root.querySelector('[data-jl-fk-out="backer"]');
    var backerLink = root.querySelector('[data-jl-fk-backerlink]');
    var productsWrap = root.querySelector('[data-jl-fk-products]');
    var productsStatus = root.querySelector('[data-jl-fk-products-status]');
    var shareBtn = root.querySelector('[data-jl-fk-share]');
    var depthEdited = false;

    function num(el) {
      if (!el) return 0;
      var v = parseFloat(String(el.value).replace(',', '.'));
      return isNaN(v) ? 0 : v;
    }

    function currentMl() {
      var K = window.JolatKalk;
      if (!K) return 0;
      return K.mlNeeded(num(inputs.bredd), num(inputs.djup), num(inputs.langd), num(inputs.spill));
    }

    /* ---- Beräkning + rendering ---- */
    function recalc() {
      var K = window.JolatKalk;
      if (!K) return;
      var bredd = num(inputs.bredd);
      var ml = currentMl();

      // Total åtgång
      if (volumeOut) {
        if (ml > 0) {
          var litersFmt = (ml / 1000).toLocaleString('sv-SE', { maximumFractionDigits: 2 });
          volumeOut.textContent = Math.round(ml).toLocaleString('sv-SE') + ' ml (' + litersFmt + ' l)';
        } else {
          volumeOut.textContent = '—';
        }
      }

      // Bottningslist-rekommendation
      if (backerOut) {
        if (bredd > 0) {
          var rod = K.backerRod(bredd);
          var len = K.backerRodLength(num(inputs.langd));
          // Locale-mallen: "Fogbredd [bredd] mm → bottningslist [diam] mm, ca [langd] m".
          // Utan foglängd visas bara diameter-rekommendationen (kapa bort " , ca … m").
          var tmpl = strings.backer || 'Fogbredd [bredd] mm → bottningslist [diam] mm, ca [langd] m';
          var text;
          if (len > 0) {
            text = fillTemplate(tmpl, {
              '[bredd]': String(Math.round(bredd)),
              '[diam]': String(rod.standard),
              '[langd]': len.toLocaleString('sv-SE', { maximumFractionDigits: 1 })
            });
          } else {
            text = fillTemplate(tmpl, { '[bredd]': String(Math.round(bredd)), '[diam]': String(rod.standard) })
              .replace(/,?\s*ca\s*\[langd\]\s*m\s*$/i, '');
          }
          backerOut.textContent = text;
          backerOut.hidden = false;
          if (backerLink) backerLink.hidden = false;
        } else {
          backerOut.hidden = true;
          if (backerLink) backerLink.hidden = true;
        }
      }

      renderProducts();
    }

    /* ---- Djup-autoförslag (tills användaren ändrat manuellt) ---- */
    function autoDepth() {
      if (depthEdited || !window.JolatKalk || !inputs.djup) return;
      var bredd = num(inputs.bredd);
      if (bredd > 0) inputs.djup.value = window.JolatKalk.suggestDepth(bredd);
    }

    /* ---- Produktförslag ur guide-datasetet ---- */
    function isAccessory(p) { return isAccessoryType(p.typ); }

    function filterByApp() {
      if (!dataset || !currentApp) return [];
      return dataset.filter(function (p) {
        if (isAccessory(p)) return false;
        return Array.isArray(p.applikation) && p.applikation.indexOf(currentApp) !== -1;
      }).sort(function (a, b) {
        var sa = a.lager === true ? 1 : 0, sb = b.lager === true ? 1 : 0;
        if (sa !== sb) return sb - sa;
        var ra = parseFloat(a.rorelse) || 0, rb = parseFloat(b.rorelse) || 0;
        return rb - ra;
      });
    }

    function productCardMarkup(p, packs) {
      var img = p.img
        ? '<img class="jl-fg-card__img" src="' + esc(p.img) + '" alt="' + esc(p.title) + '" loading="lazy" width="400" height="400">'
        : noimgMarkup(p);

      var packLine = '';
      var cta;
      if (packs > 0) {
        var packsTmpl = strings.packs || '[antal] förpackningar';
        packLine = '<p class="jl-fk-card__packs"><strong>' + esc(fillTemplate(packsTmpl, { '[antal]': String(packs) })) + '</strong></p>';
        cta = '<button type="button" class="jl-btn jl-btn--full" data-jl-fk-add data-handle="' + esc(p.handle) + '" data-qty="' + packs + '">' +
              esc(strings.addToCart || 'Lägg i varukorgen') + '</button>';
      } else {
        packLine = '<p class="jl-fk-card__packs jl-hint">' + esc(strings.fillDims || 'Fyll i mått för att räkna antal förpackningar.') + '</p>';
        cta = '<a class="jl-btn jl-btn--secondary jl-btn--full" href="' + esc(p.url) + '">' + esc(strings.showProduct || 'Visa produkt') + '</a>';
      }

      return '<article class="jl-card jl-fg-card">' +
        '<a class="jl-fg-card__media" href="' + esc(p.url) + '">' + img + '</a>' +
        '<div class="jl-fg-card__body">' +
          (p.vendor ? '<span class="jl-fg-card__vendor">' + esc(p.vendor) + '</span>' : '') +
          '<a class="jl-fg-card__title" href="' + esc(p.url) + '">' + esc(p.title) + '</a>' +
          stockMarkup(p, strings) +
          '<div class="jl-fg-card__pricerow">' + priceMarkup(p.pris_fran, strings, true) + '</div>' +
          packLine +
          '<div class="jl-fg-card__actions">' + cta + '</div>' +
        '</div>' +
      '</article>';
    }

    function renderProducts() {
      if (!productsWrap) return;
      if (!currentApp) {
        productsWrap.innerHTML = '';
        if (productsStatus) productsStatus.textContent = strings.chooseType || 'Välj fogtyp ovan så föreslår vi produkter i lager.';
        return;
      }
      if (datasetError) {
        if (productsStatus) productsStatus.textContent = strings.error || 'Kunde inte hämta produktdata.';
        return;
      }
      if (!dataset) {
        if (productsStatus) productsStatus.textContent = strings.loading || 'Hämtar produkter …';
        return;
      }
      var list = filterByApp().slice(0, 3);
      if (!list.length) {
        productsWrap.innerHTML = '';
        if (productsStatus) productsStatus.textContent = strings.noProducts || 'Inga produktförslag för det valet.';
        return;
      }
      if (productsStatus) productsStatus.textContent = '';
      var ml = currentMl();
      productsWrap.innerHTML = list.map(function (p) {
        var packs = 0;
        if (ml > 0 && window.JolatKalk) {
          packs = window.JolatKalk.cartridges(ml, guessPackVolume(p));
        }
        return productCardMarkup(p, packs);
      }).join('');
    }

    function ensureDataset() {
      if (dataset || datasetError) { renderProducts(); return; }
      if (productsStatus) productsStatus.textContent = strings.loading || 'Hämtar produkter …';
      loadDataset(collectionUrl).then(function (data) {
        dataset = data;
        renderProducts();
      }).catch(function () {
        datasetError = true;
        renderProducts();
      });
    }

    /* ---- Add-to-cart (lägg N förpackningar av produktens billigaste variant) ---- */
    // Vi har inte variant-id i guide-datasetet → använd produktens /products/<handle> permalink
    // via Shopify variant-id-lookup genom .js-endpoint för produkten.
    var productJsonCache = {};
    function firstVariantId(handle) {
      if (productJsonCache[handle]) return Promise.resolve(productJsonCache[handle]);
      return window.fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
        .then(function (data) {
          var avail = (data.variants || []).filter(function (v) { return v.available; });
          var pick = (avail[0] || data.variants[0]);
          var id = pick && pick.id;
          productJsonCache[handle] = id;
          return id;
        });
    }

    root.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-jl-fk-add]');
      if (!btn || !root.contains(btn)) return;
      var handle = btn.getAttribute('data-handle');
      var qty = parseInt(btn.getAttribute('data-qty'), 10) || 0;
      if (!handle || qty < 1) return;
      var original = btn.textContent;
      btn.disabled = true;
      firstVariantId(handle).then(function (id) {
        if (!id) throw new Error('ingen variant');
        if (window.Jolat && window.Jolat.cart && window.Jolat.cart.add) {
          return window.Jolat.cart.add([{ id: id, quantity: qty }]);
        }
        return window.fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: id, quantity: qty }] })
        }).then(function (r) { if (!r.ok) throw new Error('add'); });
      }).then(function () {
        btn.textContent = strings.added || 'Tillagd i varukorgen';
        setTimeout(function () { btn.textContent = original; btn.disabled = false; }, 1800);
      }).catch(function () {
        btn.textContent = strings.addError || 'Något gick fel — försök igen';
        setTimeout(function () { btn.textContent = original; btn.disabled = false; }, 2200);
      });
    });

    /* ---- Fogtyp-chips ---- */
    var appFieldset = root.querySelector('[data-jl-fk-apps]');
    if (appFieldset) {
      appFieldset.addEventListener('change', function (e) {
        if (e.target && e.target.tagName === 'INPUT') {
          currentApp = e.target.value;
          ensureDataset();
        }
      });
    }

    /* ---- Inputs ---- */
    if (inputs.bredd) inputs.bredd.addEventListener('input', function () { autoDepth(); recalc(); });
    if (inputs.djup) inputs.djup.addEventListener('input', function () { depthEdited = true; recalc(); });
    if (inputs.langd) inputs.langd.addEventListener('input', recalc);
    if (inputs.spill) inputs.spill.addEventListener('input', recalc);

    /* ---- Delbar URL ---- */
    function applyQuery() {
      var params = new URLSearchParams(window.location.search);
      ['bredd', 'djup', 'langd', 'spill'].forEach(function (k) {
        if (params.has(k) && inputs[k]) {
          inputs[k].value = params.get(k);
          if (k === 'djup') depthEdited = true;
        }
      });
      var app = params.get('applikation');
      if (app) {
        var input = appFieldset && appFieldset.querySelector('input[value="' + (window.CSS && CSS.escape ? CSS.escape(app) : app.replace(/["\\]/g, '\\$&')) + '"]');
        if (input) { input.checked = true; currentApp = app; }
      }
    }

    function shareKalk() {
      var params = new URLSearchParams();
      ['bredd', 'djup', 'langd', 'spill'].forEach(function (k) {
        if (inputs[k] && inputs[k].value) params.set(k, inputs[k].value);
      });
      if (currentApp) params.set('applikation', currentApp);
      var url = window.location.origin + window.location.pathname + '?' + params.toString();
      var done = function () {
        if (!shareBtn) return;
        var original = shareBtn.textContent;
        shareBtn.textContent = strings.copied || 'Länk kopierad';
        setTimeout(function () { shareBtn.textContent = original; }, 1800);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(done, done);
      } else { done(); }
      try { window.history.replaceState({}, '', '?' + params.toString()); } catch (e) {}
    }
    if (shareBtn) shareBtn.addEventListener('click', shareKalk);

    /* ---- Init ---- */
    applyQuery();
    autoDepth();
    recalc();
    if (currentApp) ensureDataset();

    document.addEventListener('jolat:tax:changed', function () {
      root.querySelectorAll('.jl-price__tax').forEach(function (el) { el.textContent = taxLabel(strings); });
    });
  }

  /* ============================================================== */
  /* Bootstrap                                                       */
  /* ============================================================== */
  function boot() {
    document.querySelectorAll('[data-jl-fogguide]').forEach(initGuide);
    document.querySelectorAll('[data-jl-fogkalk]').forEach(initKalk);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();